import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faDownload,
  faPlus,
  faSpinner,
  faTimes,
  faFileExcel,
  faFilePdf,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { reportService } from "../services/report";
import { productParametersService } from "../services/productParameters";
import { formatDateForDisplay } from "../utils/dateUtils";
import toast from "react-hot-toast";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filters, setFilters] = useState({ searchTerm: "" });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [reportType, setReportType] = useState("pivot_embalamento");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [format, setFormat] = useState("pdf"); // 'xlsx' or 'pdf'
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);

  const reportTypes = {
    pivot_embalamento: "Pivot Embalamento",
    pivot_totais: "Pivot Totais",
    producao_parametros: "Produção",
  };

  const reportDescriptions = {
    pivot_embalamento:
      "Lista todas as encomendas do período agrupadas por data de entrega: Envio, Tipo de Queijo, Destinatário, Quantidade, Embalagem, Caixa e Observações.",
    pivot_totais:
      "Totais agregados por tipo de queijo no período: quantidade total de queijos e peso total em KG.",
    producao_parametros:
      "Todas as produções do produto selecionado no período, com os parâmetros preenchidos (lotes, quantidades, medições) organizados por sessão (Leite, Fermentação, etc.).",
  };

  useEffect(() => {
    fetchReports();
  }, [filters.searchTerm]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await productParametersService.getProductsWithParameters();
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao buscar produtos com parâmetros:", err);
      }
    };
    fetchProducts();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const data = await reportService.getReports(filters);
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao buscar relatórios:", err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenModal = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setReportType("pivot_embalamento");
    setFormat("pdf");
    setProductId("");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setReportType("pivot_embalamento");
    setStartDate("");
    setEndDate("");
    setFormat("pdf");
    setProductId("");
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Por favor, selecione as datas de início e fim.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("A data de início não pode ser maior que a data de fim.");
      return;
    }
    if (reportType === "producao_parametros" && !productId) {
      toast.error("Por favor, selecione um produto.");
      return;
    }

    try {
      setGenerating(true);
      const result = await reportService.generateReport({
        report_type: reportType,
        start_date: startDate,
        end_date: endDate,
        format,
        ...(reportType === "producao_parametros" ? { product_id: productId } : {}),
      });

      toast.success("Relatório gerado com sucesso!");
      handleCloseModal();
      fetchReports();

      if (result.report_id) {
        await reportService.downloadReport(result.report_id);
      }
    } catch (err) {
      toast.error(err.message || "Erro ao gerar relatório.");
      console.error("Erro ao gerar relatório:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      await reportService.downloadReport(reportId);
      toast.success("Download iniciado!");
    } catch (err) {
      toast.error(err.message || "Erro ao fazer download do relatório.");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Tem certeza que deseja excluir este relatório?")) return;

    try {
      await reportService.deleteReport(reportId);
      toast.success("Relatório excluído com sucesso!");
      fetchReports();
    } catch (err) {
      toast.error(err.message || "Erro ao excluir relatório.");
    }
  };

  const getReportFormat = (report) => {
    const name = report.file_name || "";
    return name.toLowerCase().endsWith(".pdf") ? "pdf" : "xlsx";
  };

  const filteredReports = reports.filter((report) => {
    if (!filters.searchTerm) return true;
    const searchLower = filters.searchTerm.toLowerCase();
    return (
      report.report_name?.toLowerCase().includes(searchLower) ||
      report.report_type?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl mb-4">Relatórios</h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar relatório..."
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleFilterChange}
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-neutral-200 rounded-lg"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-3 text-neutral-400"
            />
          </div>
          <button
            onClick={handleOpenModal}
            className="w-full sm:w-auto px-4 py-2 text-sm bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Gerar Relatório
          </button>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-neutral-400" />
              <p className="mt-2 text-neutral-500">A carregar relatórios...</p>
            </div>
          ) : filteredReports.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Nome</th>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Tipo</th>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Formato</th>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Período</th>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Data de Geração</th>
                    <th className="px-4 py-3 text-left text-sm text-neutral-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {filteredReports.map((report) => {
                    const fmt = getReportFormat(report);
                    return (
                      <tr key={report.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-4 text-sm">
                          <div className="flex items-center">
                            {fmt === "pdf" ? (
                              <FontAwesomeIcon icon={faFilePdf} className="text-red-500 mr-2" />
                            ) : (
                              <FontAwesomeIcon icon={faFileExcel} className="text-green-600 mr-2" />
                            )}
                            {report.report_name}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                            {reportTypes[report.report_type] || report.report_type}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {fmt === "pdf" ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              PDF
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              XLSX
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-neutral-600">
                          {report.start_date && report.end_date
                            ? `${formatDateForDisplay(report.start_date)} - ${formatDateForDisplay(report.end_date)}`
                            : "N/A"}
                        </td>
                        <td className="px-4 py-4 text-sm text-neutral-600">
                          {formatDateForDisplay(report.generated_at)}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleDownloadReport(report.id)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Download"
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </button>
                            <button
                              onClick={() => handleDeleteReport(report.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Excluir"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faFileExcel} className="text-4xl text-neutral-300 mb-4" />
              <p className="text-neutral-500 mb-4">Nenhum relatório encontrado.</p>
              <button
                onClick={handleOpenModal}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Gerar Primeiro Relatório
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Generate Report Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-lg font-semibold">Gerar Relatório</h2>
              <button onClick={handleCloseModal} className="text-neutral-400 hover:text-neutral-600">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Tipo de Relatório
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400"
                >
                  {Object.entries(reportTypes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product (só para relatório de Produção) */}
              {reportType === "producao_parametros" && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Produto
                  </label>
                  <select
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400"
                  >
                    <option value="">Selecione um produto</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Format Toggle */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Formato de Exportação
                </label>
                <div className="flex rounded-lg border border-neutral-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormat("pdf")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                      format === "pdf"
                        ? "bg-neutral-900 text-white"
                        : "bg-white text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormat("xlsx")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-l border-neutral-300 ${
                      format === "xlsx"
                        ? "bg-neutral-900 text-white"
                        : "bg-white text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFileExcel} />
                    Excel (XLSX)
                  </button>
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Data de Fim
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="bg-neutral-50 rounded-lg p-3 text-sm text-neutral-600">
                <p>{reportDescriptions[reportType]}</p>
                {format === "pdf" && (
                  <p className="mt-1 text-xs text-neutral-500">
                    O PDF incluirá cabeçalho, tabela organizada
                    {reportType === "pivot_embalamento"
                      ? " por data de entrega"
                      : reportType === "producao_parametros"
                      ? " por produção"
                      : " com totais"}
                    {" "}e paginação.
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-neutral-200 space-x-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 border border-neutral-300 rounded-lg"
                disabled={generating}
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={generating}
                className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    A gerar...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={format === "pdf" ? faFilePdf : faFileExcel} />
                    Gerar {format === "pdf" ? "PDF" : "Excel"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
